import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { ProductCard } from "@/components/apex/ProductCard"
import { CountdownTimer } from "@/components/apex/CountdownTimer"
import { Search, Filter, Crown, Zap } from "lucide-react"

const categories = [
  { id: "all", label: "All", count: 24 },
  { id: "drops", label: "Drops", count: 3 },
  { id: "apex-only", label: "Apex Only", count: 8 },
  { id: "hoodies", label: "Hoodies", count: 6 },
  { id: "joggers", label: "Joggers", count: 4 },
  { id: "accessories", label: "Accessories", count: 3 }
]

const liveDrops = [
  {
    id: "drop-1",
    name: "Architect Collection",
    subtitle: "Limited Edition",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop",
    dropDate: new Date(Date.now() + 4 * 60 * 60 * 1000), // 4 hours from now
    itemCount: 12,
    isApexLocked: true
  }
]

const products = [
  {
    id: "1",
    name: "Apex Kinetic Joggers",
    price: 180,
    image: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=300&h=400&fit=crop",
    isApexLocked: true,
    category: "joggers"
  },
  {
    id: "2",
    name: "Architect Hoodie",
    price: 220,
    image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=300&h=400&fit=crop",
    isApexLocked: true,
    category: "hoodies"
  },
  {
    id: "3",
    name: "Form Training Tank",
    price: 85,
    image: "https://images.unsplash.com/photo-1583900985737-6d0495555783?w=300&h=400&fit=crop",
    isLimited: true,
    category: "tops"
  },
  {
    id: "4",
    name: "Motion Crew Sweatshirt",
    price: 140,
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=300&h=400&fit=crop",
    category: "hoodies"
  },
  {
    id: "5",
    name: "Apex Performance Tee",
    price: 65,
    image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=300&h=400&fit=crop",
    isApexLocked: true,
    category: "tops"
  },
  {
    id: "6",
    name: "Urban Explorer Jacket",
    price: 280,
    image: "https://images.unsplash.com/photo-1551028719-00167b16eac5?w=300&h=400&fit=crop",
    isApexLocked: true,
    category: "outerwear"
  }
]

export function ApexShop() {
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [wishlistedItems, setWishlistedItems] = useState<string[]>([])

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === "all" || 
      (selectedCategory === "apex-only" && product.isApexLocked) ||
      product.category === selectedCategory
    
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase())
    
    return matchesCategory && matchesSearch
  })

  const toggleWishlist = (productId: string) => {
    setWishlistedItems(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    )
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-lg">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-foreground">Exclusive Shop</h1>
            <Button variant="ghost" size="sm" className="p-2">
              <Filter size={20} />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Search */}
        <div className="relative">
          <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search exclusive products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-input border-border"
          />
        </div>

        {/* Live Drops */}
        {liveDrops.map(drop => (
          <Card key={drop.id} className="bg-card-gradient border-0 overflow-hidden">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className="bg-primary text-black mb-2 animate-pulse-gold">
                    <Crown size={12} className="mr-1" />
                    Live Drop
                  </Badge>
                  <h2 className="text-lg font-semibold">{drop.name}</h2>
                  <p className="text-muted-foreground text-sm">{drop.subtitle}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="aspect-[16/9] rounded-lg overflow-hidden">
                <img
                  src={drop.image}
                  alt={drop.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <CountdownTimer 
                targetDate={drop.dropDate}
                size="sm"
              />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{drop.itemCount} exclusive items</p>
                  <p className="text-xs text-muted-foreground">Architect tier required</p>
                </div>
                <Button className="bg-primary hover:bg-primary/90 text-black font-semibold">
                  View Drop
                  <Zap size={16} className="ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Categories */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold">Categories</h3>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all duration-200
                  ${selectedCategory === category.id 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }
                `}
              >
                <span className="font-medium">{category.label}</span>
                <span className="text-xs opacity-70">({category.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {selectedCategory === "all" ? "All Products" : categories.find(c => c.id === selectedCategory)?.label}
            </h3>
            <p className="text-sm text-muted-foreground">
              {filteredProducts.length} items
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {filteredProducts.map(product => (
              <ProductCard
                key={product.id}
                {...product}
                isWishlisted={wishlistedItems.includes(product.id)}
                onWishlist={() => toggleWishlist(product.id)}
                onAddToCart={() => {}}
              />
            ))}
          </div>
        </div>

        {/* Empty State */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <div className="text-muted-foreground mb-4">
              <Search size={48} className="mx-auto opacity-20" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No products found</h3>
            <p className="text-muted-foreground text-sm">
              Try adjusting your search or category filters
            </p>
          </div>
        )}
      </div>
    </div>
  )
}